<template>
  <div>
    <!-- MOBILE REAL (< 600px): Dialog fullscreen simples -->
    <template v-if="width < 600">
      <v-btn 
        icon 
        variant="text"
        :color="hasUnreadNotifications ? 'info' : undefined"
        :style="!hasUnreadNotifications ? 'color: #FEF3C7E6' : ''"
        @click="dialog = true"
      >
        <v-badge 
          v-if="hasUnreadNotifications" 
          :content="unreadCount" 
          color="info"
          overlap
        >
          <v-icon>mdi-bell</v-icon>
        </v-badge>
        <v-icon v-else>mdi-bell-outline</v-icon>
      </v-btn>

      <v-dialog 
        v-model="dialog" 
        max-width="400px"
        :scrim="false"
      >
        <v-card class="notification-card-mobile d-flex flex-column h-100" rounded="xl">
          <!-- Header simples fixo -->
          <v-card-title class="notification-header d-flex justify-space-between align-center pa-2 flex-shrink-0">
            <span class="notification-title">Notificações</span>
            <div class="d-flex ga-2">
              <v-btn
                v-if="hasUnreadNotifications"
                variant="text"
                color="white"
                size="small"
                @click="handleMarkAllAsRead"
                :loading="markingAsRead"
                rounded="lg"
              >
                Marcar todas
              </v-btn>
              <v-btn 
                icon="mdi-close" 
                variant="text"
                color="white"
                size="small"
                @click="dialog = false"
                rounded="lg"
              />
            </div>
          </v-card-title>

          <!-- Conteúdo das notificações -->
          <v-card-text class="notification-content pa-0 flex-grow-1 overflow-y-auto">
            <template v-if="notifications.length">
              <v-list class="notification-list" bg-color="transparent">
                <v-list-item
                  v-for="(notification, index) in notifications"
                  :key="notification.id"
                  :class="{ 'notification-read': notification.is_read }"
                  @click="handleNotificationClick(notification)"
                  class="notification-item py-4 px-4"
                  rounded="lg"
                >
                  <template #prepend>
                    <v-avatar size="55" class="me-2 notification-avatar">
                      <v-img 
                        :src="notification.last_actor?.profile_image_url"
                        :alt="notification.last_actor?.username"
                      >
                        <template #placeholder>
                          <v-img src="/pp-placeholder.png" />
                        </template>
                      </v-img>
                    </v-avatar>
                  </template>

                  <div class="notification-text-content">
                    <div class="notification-main-text mb-1">
                      {{ getNotificationTitle(notification) }}
                    </div>
                    
                    <div 
                      v-if="getNotificationBody(notification)"
                      class="notification-sub-text"
                    >
                      {{ getNotificationBody(notification) }}
                    </div>
                  </div>

                  <template #append>
                    <div class="d-flex flex-column align-end ga-2">
                      <v-chip
                        v-if="!notification.is_read"
                        color="primary"
                        size="small"
                        variant="flat"
                        rounded="lg"
                      >
                        Nova
                      </v-chip>
                      <span class="notification-time">
                        {{ formatNotificationTime(notification.created_at) }}
                      </span>
                    </div>
                  </template>
                </v-list-item>
              </v-list>
            </template>

            <div 
              v-else 
              class="notification-empty d-flex flex-column align-center justify-center h-100 pa-8"
            >
              <v-icon size="64" color="disabled" class="mb-4">
                mdi-bell-off-outline
              </v-icon>
              <span class="notification-empty-title mb-2">
                Nenhuma notificação
              </span>
              <span class="notification-empty-text">
                Você está em dia!
              </span>
            </div>
          </v-card-text>
        </v-card>
      </v-dialog>
    </template>

    <!-- TODAS AS OUTRAS TELAS (>= 600px): Dropdown -->
    <template v-else>
      <v-menu 
        v-model="menu" 
        location="bottom end" 
        :offset="12"
        :close-on-content-click="false"
        max-width="420"
      >
        <template #activator="{ props }">
            <v-btn 
            icon 
            variant="text" 
            v-bind="props"
            :color="hasUnreadNotifications ? 'info' : undefined"
            :style="!hasUnreadNotifications ? 'color: #FEF3C7E6' : ''"
            >
            <v-badge 
              v-if="hasUnreadNotifications" 
              :content="unreadCount" 
              color="info"
              overlap
            >
              <v-icon>mdi-bell</v-icon>
            </v-badge>
            <v-icon v-else>mdi-bell-outline</v-icon>
          </v-btn>
        </template>

        <v-card rounded="xl" class="notification-card-desktop elevation-12">
          <!-- Header -->
          <v-card-title class="notification-header d-flex justify-space-between align-center pa-3">
            <span class="notification-title">Notificações</span>
            <div class="d-flex ga-2">
              <v-btn
                v-if="hasUnreadNotifications"
                variant="tonal"
                color="success"
                size="small"
                @click="handleMarkAllAsRead"
                :loading="markingAsRead"
                rounded="lg"
              >
                Marcar todas
              </v-btn>
              <v-btn 
                icon="mdi-close" 
                variant="text" 
                size="small"
                @click="menu = false"
                rounded="lg"
              />
            </div>
          </v-card-title>
          
          <v-divider />

          <!-- Lista scrollável -->
          <div class="notification-scroll-area">
            <template v-if="notifications.length">
              <v-list class="notification-list" bg-color="transparent">
                <v-list-item
                  v-for="notification in notifications"
                  :key="notification.id"
                  :class="{ 'notification-read': notification.is_read }"
                  @click="handleNotificationClick(notification)"
                  class="notification-item py-3 px-4 mx-2 my-1"
                  rounded="lg"
                >
                  <template #prepend>
                    <v-avatar size="50" class="me-2 notification-avatar">
                      <v-img 
                        :src="notification.last_actor?.profile_image_url" 
                        :alt="notification.last_actor?.username"
                      >
                        <template #placeholder>
                          <v-img src="/pp-placeholder.png" />
                        </template>
                      </v-img>
                    </v-avatar>
                  </template>

                  <div class="notification-text-content">
                    <div class="notification-main-text mb-1">
                      {{ getNotificationTitle(notification) }}
                    </div>
                    
                    <div 
                      v-if="getNotificationBody(notification)"
                      class="notification-sub-text"
                    >
                      {{ getNotificationBody(notification) }}
                    </div>
                  </div>

                  <template #append>
                    <div class="d-flex flex-column align-end ga-1">
                      <v-chip
                        v-if="!notification.is_read"
                        color="primary"
                        size="x-small"
                        variant="flat"
                        rounded="lg"
                      >
                        Nova
                      </v-chip>
                      <span class="notification-time">
                        {{ formatNotificationTime(notification.created_at) }}
                      </span>
                    </div>
                  </template>
                </v-list-item>
              </v-list>
            </template>

            <div 
              v-else 
              class="notification-empty d-flex flex-column align-center justify-center pa-8"
            >
              <v-icon size="48" color="disabled" class="mb-3">
                mdi-bell-off-outline
              </v-icon>
              <span class="notification-empty-title">
                Nenhuma notificação
              </span>
            </div>
          </div>
        </v-card>
      </v-menu>
    </template>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useNotificationsStore } from '@/stores/notifications'
import { storeToRefs } from 'pinia'
import { useDisplay } from 'vuetify'

// State
const menu = ref(false)
const dialog = ref(false)
const markingAsRead = ref(false)

// Composables
const router = useRouter()
const { width } = useDisplay()
const notificationsStore = useNotificationsStore()
const { notifications } = storeToRefs(notificationsStore)

// Computed
const hasUnreadNotifications = computed(() => 
  notifications.value.some(n => n.is_read === 0)
)
const unreadCount = computed(() => 
  notifications.value.filter(n => n.is_read === 0).length
)

// Methods
async function handleMarkAllAsRead() {
  if (markingAsRead.value) return
  
  markingAsRead.value = true
  try {
    await notificationsStore.markAllAsRead()
  } catch (error) {
    console.error('Erro ao marcar todas como lidas:', error)
  } finally {
    markingAsRead.value = false
  }
}

async function handleNotificationClick(notification) {
  try {
    if (notification.is_read === 0) {
      await notificationsStore.markAsRead([notification.id])
    }
    
    menu.value = false
    dialog.value = false
    
    navigateToNotification(notification)
    
  } catch (error) {
    console.error('Erro ao processar notificação:', error)
  }
}

function navigateToNotification(notification) {
  const { type, last_actor, entity_id, secondary_entity_id } = notification
  
  const routes = {
    'new_follower': () => router.push({
      name: 'ProfileBeta',
      params: { username: last_actor.username }
    }),
    'record_like': () => router.push({
      name: 'RecordPage',
      params: { id: entity_id }
    }),
    'record_dislike': () => router.push({
      name: 'RecordPage',
      params: { id: entity_id }
    }),
    'comment': () => router.push({
      name: 'RecordPage',
      params: { id: entity_id }
    }),
    'record_comment_like': () => router.push({
      name: 'RecordPage',
      params: { id: secondary_entity_id }
    }),
    'record_comment_dislike': () => router.push({
      name: 'RecordPage',
      params: { id: secondary_entity_id }
    })
  }
  
  const navigate = routes[type]
  if (navigate) {
    navigate()
  }
}

function getNotificationTitle(notification) {
  const { last_actor, count, type } = notification
  const username = `@${last_actor.username}`
  const isPlural = count > 1
  const others = isPlural ? ` e mais ${count - 1}` : ''
  const actor = `${username}${others}`

  const messages = {
    'new_follower': `${actor} ${isPlural ? 'começaram' : 'começou'} a seguir você`,
    'record_like': `${actor} ${isPlural ? 'curtiram' : 'curtiu'} sua avaliação`,
    'record_dislike': `${actor} ${isPlural ? 'descurtiram' : 'descurtiu'} sua avaliação`,
    'comment': `${actor} ${isPlural ? 'comentaram' : 'comentou'} em sua avaliação`,
    'record_comment_like': `${actor} ${isPlural ? 'curtiram' : 'curtiu'} seu comentário`,
    'record_comment_dislike': `${actor} ${isPlural ? 'descurtiram' : 'descurtiu'} seu comentário`
  }

  return messages[type] || 'Nova notificação'
}

function getNotificationBody(notification) {
  if (!notification.entity_text) return ''
  
  const MAX_LENGTH = 60
  const text = notification.entity_text.trim()
  
  return text.length <= MAX_LENGTH ? `"${text}"` : `"${text.slice(0, MAX_LENGTH)}..."`
}

function formatNotificationTime(dateString) {
  if (!dateString) return ''
  
  const now = new Date()
  const date = new Date(dateString)
  const diffInHours = Math.floor((now - date) / (1000 * 60 * 60))
  
  if (diffInHours < 1) return 'Agora'
  if (diffInHours < 24) return `${diffInHours}h`
  
  const diffInDays = Math.floor(diffInHours / 24)
  if (diffInDays < 7) return `${diffInDays}d`
  
  return date.toLocaleDateString('pt-BR', { 
    day: '2-digit', 
    month: '2-digit' 
  })
}

onMounted(() => {
  notificationsStore.fetchNotifications()
})
</script>

<style scoped>
/* Cards principais */
.notification-card-mobile,
.notification-card-desktop {
  background: rgba(20, 30, 48, 0.95);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(52, 120, 134, 0.2);
}

.notification-card-desktop {
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
}

/* Header */
.notification-header {
  background: linear-gradient(135deg, #0E343E 0%, #1a4f5c 50%, #2d6a75 100%);
  color: white;
  border-radius: 16px 16px 0 0;
}

.notification-title {
  font-size: 1.25rem;
  font-weight: 600;
  letter-spacing: -0.025em;
}

/* Área de scroll */
.notification-scroll-area {
  max-height: 420px;
  overflow-y: auto;
  background: rgba(16, 42, 50, 0.9);
}

/* Lista */
.notification-list {
  padding: 8px 0;
}

/* Item de notificação */
.notification-item {
  cursor: pointer;
  transition: all 0.2s ease;
  background: rgba(26, 60, 72, 0.7);
  margin: 2px 8px;
  border: 1px solid rgba(52, 120, 134, 0.3);
}

.notification-item:hover {
  background: rgba(35, 80, 95, 0.9);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
}

.notification-read {
  opacity: 0.65;
}

.notification-read:hover {
  opacity: 0.85;
}

/* Avatar */
.notification-avatar {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

/* Textos */
.notification-text-content {
  flex: 1;
  min-width: 0;
}

.notification-main-text {
  font-size: 0.95rem;
  font-weight: 500;
  line-height: 1.4;
  color: #e2e8f0;
  word-wrap: break-word;
}

.notification-sub-text {
  font-size: 0.85rem;
  color: #94a3b8;
  line-height: 1.3;
  word-wrap: break-word;
}

.notification-time {
  font-size: 0.75rem;
  color: #64748b;
  font-weight: 500;
}

/* Estado vazio */
.notification-empty {
  background: rgba(16, 42, 50, 0.5);
  border-radius: 12px;
  margin: 16px;
}

.notification-empty-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: #94a3b8;
}

.notification-empty-text {
  font-size: 0.9rem;
  color: #64748b;
}

/* Scrollbar customizada */
.notification-scroll-area::-webkit-scrollbar {
  width: 6px;
}

.notification-scroll-area::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.1);
  border-radius: 3px;
}

.notification-scroll-area::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.3);
  border-radius: 3px;
}

.notification-scroll-area::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.5);
}
</style>